import { breadcrumbSchema } from "@/lib/schema";
import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { Picture } from "@/components/site/Picture";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { CTAButton } from "@/components/site/Button";
import { StrategyCallButton } from "@/components/site/StrategyCallDialog";
import { BRAND } from "@/lib/brand";
import roundtableImg from "@/assets/roundtable.jpg";
import { DirectAnswer, FAQSection, faqSchema } from "@/components/site/AnswerBlock";
import { ROUNDTABLE_ANSWER, ROUNDTABLE_FAQS } from "@/lib/aeo";
import { ValuesHeading } from "@/components/site/ValuesSection";

export const Route = createFileRoute("/round-table")({
  head: () => ({
    meta: [
      { title: "Real Estate Mastermind - Monthly Market Round Table" },
      {
        name: "description",
        content:
          "An invite-only real estate mastermind with Kristan Cole: market shifts, scripts, systems, and a real answer to real estate agent burnout. Request an invitation.",
      },
      { property: "og:title", content: "The Monthly Market Round Table" },
      {
        property: "og:description",
        content:
          "Invite-only. Monthly. The room where Kristan coaches her team - and you get a seat.",
      },
      { property: "og:url", content: "https://joinkcn.com/round-table" },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/round-table" }],
    scripts: [
      breadcrumbSchema([{ name: "Monthly Market Round Table", path: "/round-table" }]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          name: "Kristan Cole Network Monthly Market Round Table",
          eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
          eventSchedule: {
            "@type": "Schedule",
            repeatFrequency: "P1M",
            byMonth: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          },
          organizer: { "@type": "Organization", name: "Kristan Cole Network" },
          performer: { "@type": "Person", name: "Kristan Cole" },
          description:
            "Monthly invite-only virtual round table for real estate agents - market intel, scripts, systems, and live coaching from Kristan Cole.",
          offers: {
            "@type": "Offer",
            url: "https://kcnroundtable.com/",
            availability: "https://schema.org/InviteOnly",
            price: "0",
            priceCurrency: "USD",
          },
          location: {
            "@type": "VirtualLocation",
            url: "https://kcnroundtable.com/",
          },
        }),
      },
      faqSchema(ROUNDTABLE_FAQS, "https://joinkcn.com/round-table"),
    ],
  }),
  component: RoundTablePage,
});

function RoundTablePage() {
  return (
    <SiteLayout>
      <section className="relative isolate min-h-[90svh] flex items-end overflow-hidden">
        <Picture
          src={roundtableImg}
          alt="Real estate team meeting around a conference table"
          pictureClassName="absolute inset-0 block h-full w-full"
          className="h-full w-full object-cover"
          width={1920}
          height={1280}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        <div className="relative container-edge pb-14 pt-36 w-full">
          <p className="eyebrow text-foreground/90 mb-6">Invitation Only · Monthly</p>
          <h1 className="headline-xl max-w-5xl">
            The Monthly Market
            <br />
            <span className="serif-italic text-primary">Round Table.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-foreground/90 leading-relaxed">
            Once a month, Kristan Cole gathers her national team for the same
            coaching session. Market shifts. Scripts that are working. Systems
            being installed. Strategy at the CEO level. Now - by invitation - you
            can sit in.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <CTAButton href={BRAND.links.roundTable} size="lg">
              Request Your Invitation
            </CTAButton>
            <StrategyCallButton variant="outline" size="lg">
              Or Book a Growth Strategy Call
            </StrategyCallButton>
          </div>
        </div>
      </section>

      {/* 92% stat + Does this describe you? */}
      <Section tone="darker">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="font-display text-[7rem] md:text-[10rem] leading-none text-primary tracking-tight">
              92<span className="text-foreground/85">%</span>
            </p>
            <p className="mt-4 text-foreground/80 text-lg leading-relaxed max-w-md">
              of agents in the nation did under{" "}
              <strong className="text-foreground">4 transactions</strong> last
              year. The Round Table exists for the other side of that number.
            </p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionEyebrow>Does this describe you?</SectionEyebrow>
            <ValuesHeading className="headline-md" />

            <p className="mt-6 text-foreground/90 text-lg leading-relaxed">
              If you read that and felt something click - you're who this room
              is built for. Request your invitation and come sit in.
            </p>
            <div className="mt-8">
              <CTAButton href={BRAND.links.roundTable} size="lg">
                Request Your Invitation
              </CTAButton>
            </div>
          </div>
        </div>
      </Section>

      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>What you'll hear</SectionEyebrow>
            <h2 className="headline-lg">
              Two hours of market data.
              <br />
              <span className="serif-italic text-primary">Then the scripts</span> that
              turn it into closings.
            </h2>
            <p className="mt-6 text-foreground/85 leading-relaxed text-lg">
              The agents who own their market are the ones who understand it.
              That's why every month starts with the data.
            </p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="divide-y divide-border border-y border-border">
              {[
                "The market data breakdown that opens every session - pricing, days on market, absorption, financing shifts",
                "The scripts and conversations winning conversion this month",
                "Systems and SOPs Kristan is installing across the team",
                "Hiring, leadership, and team-building decisions in real time",
                "Faith, family, and CEO-level thinking - without the hype",
                "Live Q&A with Kristan and team leaders",
              ].map((line) => (
                <li key={line} className="py-5 text-lg text-foreground/85 flex gap-4">
                  <span className="text-primary text-xs mt-2">◆</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>

          </div>
        </div>
      </Section>

      <Section tone="cream">
        <div className="mb-12">
          <SectionEyebrow>Invitation process</SectionEyebrow>
          <h2 className="headline-md text-cream-foreground">
            How to join the Round Table
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-12">
          {[
            {
              n: "01",
              t: "Request",
              b: "Submit your invitation request through the form on this site. A few quick questions about your business, goals, and season.",
            },
            {
              n: "02",
              t: "Review",
              b: "Our team reviews each request to make sure the room stays focused. Approved guests are notified by email.",
            },
            {
              n: "03",
              t: "Attend",
              b: "You'll receive the access link for the next monthly session. Show up. Take notes. Bring your real questions.",
            },
          ].map((s) => (
            <div key={s.n}>
              <p className="text-primary text-xs uppercase tracking-[0.25em] mb-6">
                {s.n}
              </p>
              <h3 className="font-display text-3xl mb-4 text-cream-foreground">
                {s.t}
              </h3>
              <p className="text-cream-foreground/85 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="darker">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionEyebrow>Who it's for</SectionEyebrow>
            <h2 className="headline-md">
              Growing and top-producing agents who want to see how a $1B+ team
              actually operates - from the inside.
            </h2>
            <p className="mt-6 text-foreground/90 text-lg leading-relaxed">
              The Round Table isn't a webinar. It's a working session. You'll
              see, hear, and feel exactly what it's like to be coached at the
              level Kristan coaches her own team.
            </p>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="border border-border p-10 bg-background">
              <p className="eyebrow mb-6">Ready?</p>
              <h3 className="font-display text-3xl mb-6">
                Request your invitation.
              </h3>
              <CTAButton
                href={BRAND.links.roundTable}
                size="lg"
                className="w-full"
              >
                Request Your Invitation
              </CTAButton>
              <p className="mt-6 text-xs text-foreground/55 leading-relaxed">
                Approvals reviewed each week. You'll receive your access link by
                email before the next session.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <DirectAnswer question={ROUNDTABLE_ANSWER.question} answer={ROUNDTABLE_ANSWER.answer} />

      <FAQSection
        faqs={ROUNDTABLE_FAQS}
        eyebrow="Round Table questions"
        heading="What to expect."
      />

      <CTABanner
        cta="strategy"
        eyebrow="Prefer a private conversation?"
        title="Book a 1:1 Growth Strategy Session with our team."
        body="If you'd rather skip the Round Table and talk directly, book a private session. We'll walk through your business and where KCN may - or may not - fit."
      />
    </SiteLayout>
  );
}
